import type { inferRouterInputs } from "@trpc/server";
import type { ZodFormattedError } from "zod";
import type { AppRouter } from "@app-trpc/server/api/trpc/[trpc]";

export type CreateJobApplicationDataInput =
  inferRouterInputs<AppRouter>["career"]["jobs"]["createApplication"];
export type UpdateCareerBenefitsDataInput =
  inferRouterInputs<AppRouter>["career"]["benefits"]["updateBenefits"];
export type UpdateTeamValuesDataInput =
  inferRouterInputs<AppRouter>["career"]["team"]["updateAllValues"];
export type UpdateTeamDescriptionScreenSettingsDataInput =
  inferRouterInputs<AppRouter>["career"]["team"]["updateTeamDescriptionScreenSettings"];
export type UpdateTeamValuesScreenSettingsDataInput =
  inferRouterInputs<AppRouter>["career"]["team"]["updateTeamValuesScreenSettings"];

export const useCareersClient = () => {
  const client = useNuxtApp().$client;

  async function getBenefits() {
    return client.career.benefits.getBenefits.query();
  }

  async function updateBenefits(input: UpdateCareerBenefitsDataInput) {
    try {
      await client.career.benefits.updateBenefits.mutate(input);
    } catch (e) {
      if (isTRPCClientError(e)) {
        const zodError = e.data
          ?.zodError as ZodFormattedError<UpdateCareerBenefitsDataInput>;
        if (zodError)
          return {
            validationErrors: zodError,
          };
      } else {
      }
    }
  }

  async function findOpened() {
    return client.career.jobs.findOpened.query();
  }

  async function findById(id: number) {
    return client.career.jobs.findById.query(id);
  }

  async function createApplication(
    data: CreateJobApplicationDataInput
  ): Promise<
    | {
        validationErrors: ZodFormattedError<CreateJobApplicationDataInput>;
      }
    | undefined
  > {
    try {
      await client.career.jobs.createApplication.mutate(data);
    } catch (e) {
      if (isTRPCClientError(e)) {
        // TODO: simplify
        const zodError = e.data
          ?.zodError as ZodFormattedError<CreateJobApplicationDataInput>;
        return {
          validationErrors: zodError,
        };
      }
    }
  }

  async function getAllTeamValues() {
    return client.career.team.getAllValues.query();
  }

  async function updateAllTeamValues(input: UpdateTeamValuesDataInput) {
    return client.career.team.updateAllValues.mutate(input);
  }

  async function getTeamDescriptionScreenSettings() {
    return client.career.team.getTeamDescriptionScreenSettings.query();
  }

  async function updateTeamDescriptionScreenSettings(
    input: UpdateTeamDescriptionScreenSettingsDataInput
  ) {
    return client.career.team.updateTeamDescriptionScreenSettings.mutate(input);
  }

  async function getTeamValuesScreenSettings() {
    return client.career.team.getTeamValuesScreenSettings.query();
  }

  async function updateTeamValuesScreenSettings(
    input: UpdateTeamValuesScreenSettingsDataInput
  ) {
    return client.career.team.updateTeamValuesScreenSettings.mutate(input);
  }

  return {
    findOpened,
    findById,
    createApplication,
    getBenefits,
    updateBenefits,
    getAllTeamValues,
    updateAllTeamValues,
    getTeamDescriptionScreenSettings,
    updateTeamDescriptionScreenSettings,
    getTeamValuesScreenSettings,
    updateTeamValuesScreenSettings,
  };
};
